import { Injectable } from "@angular/core";
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { Store } from "@ngrx/store";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import * as NotificationActions from "../../notifications/actions/notification.actions";

type ApiErrorPayload = {
  message?: string;
  status?: number;
  traceId?: string;
};

@Injectable()
export class ApiErrorInterceptor implements HttpInterceptor {
  constructor(private store: Store) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const payload = (error?.error ?? {}) as ApiErrorPayload;

        const status = error?.status ?? payload?.status ?? 0;
        const traceId = payload?.traceId;
        const apiMessage = payload?.message;

        // Avoid duplicate UX for flows that already handle errors
        if (this.shouldIgnore(req.url, status)) {
          return throwError(() => error);
        }

        // Only notify for "important" cases to avoid spamming users
        if (!this.shouldNotify(status)) {
          return throwError(() => error);
        }

        const userMessage = this.toUserMessage(status, apiMessage);
        const messageWithRef = traceId
          ? `${userMessage} (Ref: ${traceId})`
          : userMessage;

        // eslint-disable-next-line no-console
        console.error("API error", {
          url: req.url,
          method: req.method,
          status,
          message: apiMessage,
          traceId,
        });

        const actionLabel = traceId ? "Copy ref" : "Dismiss";

        this.store.dispatch(
          NotificationActions.snackbarOpen({
            message: messageWithRef,
            action: actionLabel,
            copyText: traceId,
            config: {
              duration: status === 0 || status >= 500 ? 8000 : 5000,
              horizontalPosition: "right",
              verticalPosition: "bottom",
            },
          }),
        );

        return throwError(() => error);
      }),
    );
  }

  private shouldIgnore(url: string, status: number): boolean {
    // DOCX download path already has bespoke UX
    if (url.includes("/api/consultantprofile/docx/")) return true;

    // Missing photos are expected; do not toast on 404 for this endpoint (even if we ever change 404 policy later)
    if (status === 404 && url.includes("/api/contents/profilephoto/"))
      return true;

    return false;
  }

  private shouldNotify(status: number): boolean {
    // IMPORTANT-ONLY policy:
    // - Network/connection issues
    // - Auth/permission problems
    // - Conflict/business-rule problems
    // - Server errors
    if (status === 0) return true;
    if (status === 401) return true;
    if (status === 403) return true;
    if (status === 409) return true;
    if (status >= 500) return true;

    // Explicitly suppress 404 and other 4xx by default (avoid snackbar spam)
    return false;
  }

  private toUserMessage(status: number, apiMessage?: string): string {
    if (status === 0)
      return "Network error. Please check your connection and try again.";
    if (status === 401)
      return "You are not signed in. Please sign in and try again.";
    if (status === 403)
      return "You do not have permission to perform this action.";
    if (status === 409)
      return (
        apiMessage || "This action could not be completed due to a conflict."
      );
    if (status >= 500)
      return apiMessage || "A server error occurred. Please try again.";
    return apiMessage || "An unexpected error occurred.";
  }
}
